// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// material styles
import {
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  deleteServiceByIdRequest,
  getServiceByIdRequest,
  getServiceSubCategoryRequest,
  updateServiceRequest,
} from "api/requests/services";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./editService.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";
import { useNavigate, useParams } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import DeleteModal from "components/shared/Modal/Delete";
import BackButton from "components/shared/BackButton";

// services categories...
const categoryList = [
  { value: "consultation", title: "مشاوره" },
  { value: "performance", title: "اجرا" },
  { value: "supervision", title: "نظارت" },
  { value: "export-import-clearance", title: "صادرات واردات و ترخیص" },
];

export default function EditService() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();
  const { service_id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [serviceImage, setServiceImage] = useState({
    url: null,
    isChanged: false,
  });
  const [serviceVideo, setServiceVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // select option state
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [allSubCategories, setAllSubCategories] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //handle delete modal
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete service by id
  const handleDeleteItem = useCallback(
    async (id) => {
      setLoading(false);
      const res = await request({
        url: () => deleteServiceByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف خدمات " });
        navigate("/services");
      }
      handelShowModal();
    },
    [setLoading]
  );

  // edit service
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!serviceImage.url) return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        updateServiceRequest(
          {
            id: service_id,
            title: data.title,
            subcategory_id: selectedSubCategory,
            text,
            alt: data.alt,
            meta_description: data.meta_description,
            keyword: data.keyword,
            show_in_home_page: checked ? 1 : 0,
            main_image: serviceImage,
            video: serviceVideo,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "ویرایش خدمات" });
      navigate("/services");
    }
  };

  // uploaded image handler method
  const handleChangeServicesImage = (val) => {
    validateImg(val);
    setServiceImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeServicesVideo = (val) =>
    setServiceVideo({ url: val, isChanged: true });

  // get service by id data
  const getServiceByIdData = useCallback(async () => {
    setLoading(true);
    const subCategoryRes = await request({
      url: getServiceSubCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });

    if (subCategoryRes) {
      setAllSubCategories({
        data: subCategoryRes.data.data,
        count: subCategoryRes.data.count,
      });
    }
    const serviceRes = await request({
      url: () => getServiceByIdRequest({ id: service_id }),
      isMulty: true,
    });

    if (serviceRes) {
      let data = serviceRes.data;
      setText(data.text);
      if (data.main_image) {
        setServiceImage({ url: data?.main_image, isWeb: true });
      }
      if (data.video) {
        setServiceVideo({ url: data?.video, isWeb: true });
      }
      setChecked(data?.show_in_home_page);
      reset({
        title: data?.title,
        alt: data?.image_details?.alt,
        meta_description: data?.seo_options?.meta_description,
        keyword: data?.seo_options?.keyword,
      });
      if (data.subcategory_id) {
        setSelectedSubCategory(data.subcategory_id);
      }
      if (data.services_sub_categories) {
        if (data.services_sub_categories.category) {
          setSelectedCategory(data.services_sub_categories.category);
        }
        data.subcategory_id &&
          reset({
            title: data.title,
            alt: data?.image_details?.alt,
            meta_description: data?.seo_options?.meta_description,
            keyword: data?.seo_options?.keyword,
            category: data.services_sub_categories.category,
            subCategory: data.subcategory_id,
          });
        !data.subcategory_id &&
          reset({
            title: data.title,
            alt: data?.image_details?.alt,
            meta_description: data?.seo_options?.meta_description,
            keyword: data?.seo_options?.keyword,
            category: data.services_sub_categories.category,
          });
      }
      setLoading(false);
    } else return Toast({ type: "err", title: "دریافت اطلاعات خدمات" });
  }, [request]);

  useEffect(() => {
    setSelectedSubCategory(null);
    let data = allSubCategories.data.filter(
      (item) => item.category === selectedCategory
    );

    let transformedData = data.map((item) => ({
      id: item.id,
      title: item.title,
    }));
    setSubCategoryList(transformedData);
  }, [selectedCategory]);

  useEffect(() => {
    getServiceByIdData();
  }, [getServiceByIdData]);

  return (
    <DrawerCM title="ویرایش خدمات">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/services" />
          </Grid>
          <StyledStack>
            <StyledTitle>ویرایش خدمات</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان خدمات"
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>
              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="سردسته"
                  control={control}
                  title="سردسته"
                  fullWidth
                  id="category"
                  {...register("category")}
                  error={Boolean(errors.category)}
                  helperText={errors.category?.message}
                  onChange={(value) => setSelectedCategory(value)}
                >
                  {categoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.value}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="زیردسته"
                  control={control}
                  title="زیردسته"
                  id="subCategory"
                  {...register("subCategory")}
                  error={Boolean(errors.subCategory)}
                  helperText={errors.subCategory?.message}
                  onChange={(value) => setSelectedSubCategory(value)}
                >
                  {subCategoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {serviceImage.url ? (
                  <Upload
                    isWeb={serviceImage.isWeb}
                    isLocal={!serviceImage.isWeb}
                    file={serviceImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeServicesImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={serviceImage.isChanged}
                    file={serviceImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeServicesImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {serviceVideo.url ? (
                  <UploadVideoCM
                    isWeb={serviceVideo.isWeb}
                    isLocal={!serviceVideo.isWeb}
                    file={serviceVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeServicesVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={serviceVideo.isChanged}
                    file={serviceVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeServicesVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label=" متن جایگزین"
                  variant="outlined"
                  fullWidth
                  id="alt"
                  {...register("alt")}
                  error={Boolean(errors.alt)}
                  helperText={errors.alt?.message}
                />
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                <TextField
                  label="کلمه کلیدی"
                  variant="outlined"
                  fullWidth
                  id="keyword"
                  {...register("keyword")}
                  error={Boolean(errors.keyword)}
                  helperText={errors.keyword?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                <TextField
                  label="فراتوضیحات"
                  variant="outlined"
                  fullWidth
                  id="meta_description"
                  {...register("meta_description")}
                  error={Boolean(errors.meta_description)}
                  helperText={errors.meta_description?.message}
                />
              </Grid>
              <Grid item xs={12} p={1.2}>
                {text && (
                  <Editor
                    enableToolbar={true}
                    onChange={handleChangeText}
                    defaultValue={text}
                    setDefaultStyle="font-family: IranSans; height: 40vh;"
                    setOptions={{
                      buttonList: buttonList.complex,
                      font: [
                        "IranSans",
                        "IranSansBold",
                        "Arial",
                        "Impact",
                        "Tahoma",
                      ],
                    }}
                  />
                )}
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1, mr: 2 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
                <Button
                  startIcon={<MdDelete />}
                  variant="contained"
                  color="error"
                  sx={{ mt: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handelShowModal();
                  }}
                >
                  حذف
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, service_id)}
          />
        </Grid>
      )}
    </DrawerCM>
  );
}
